<template>
  <page :loading="$apollo.queries.job.loading || !job" :err="err" layoutClass="job-show">
    <b-row v-if="job && job.id">
      <b-col md="8">
        <b-card class="bg-white job-card h-100" no-body>
          <b-card-header class="bg-white border-0" id="job-header">
            <h3>{{ job.title }}</h3>
          </b-card-header>

          <b-card-body class="py-0">
            <key-stats :job="job"/>
          </b-card-body>

          <b-card-body class="job-description">
            <!-- img v-if="job.image" class="job-image" :src="job.image"/ -->
            <markdown :source="job.description" :anchorAttributes="markdownAnchorAttributes"/>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col md="4">
        <b-card class="bg-white buttons-card px-2 px-md-3 px-xl-5" body-class="px-lg-3 px-xl-5">

          <b-btn v-if="showLinkedInSearchOnJobs" v-b-modal="`job-linkedinSearch-${job.id}`" class="w-100" variant="linkedin">
            <font-awesome-icon class="mr-2" :icon="['fab', 'linkedin']"/> {{ $t('campaign.buttons.matches') }}
          </b-btn>
          <b-btn class="mt-3 w-100" v-if="canEmployeeApply" :disabled="!absoluteShareURL"
            :href="absoluteShareURL" target="_blank" variant="primary">{{ $t('campaign.buttons.apply') }}</b-btn>
          <b-btn class="mt-3 w-100" variant="primary" v-b-modal="`job-actions-${job.id}`">{{ $t('campaign.buttons.refer') }}</b-btn>
          <b-btn v-if="isRecruiter" :to="{ name: 'JobEdit', params: { jobID: job.id } }" class="mt-3 w-100" variant="primary">
            <font-awesome-icon class="mr-2" icon="pencil"/> {{ $t('campaign.buttons.edit') }}
          </b-btn>

          <b-modal :id="`job-linkedinSearch-${job.id}`" :title="$t('campaign.search-linkedin.title')" hide-footer lazy v-if="showLinkedInSearchOnJobs">
            <linkedin-search-details :jobID="job.id" :jobTitle="job.title" :shareUrl="absoluteShareURL"
              :linkedInSearchURL="(job.customFieldsHash ? job.customFieldsHash.linkedInSearchURL : null) || job.linkedInSearchURL" :preview-text="jobShareText" :preview-args="{ jobTitle: job.title }"
              @trackedLiBrowseEvent="trackedLiBrowseEvent()"
              @trackShareEvent="({ channel }) => trackJobSharedEvent({ channel })"
            />
          </b-modal>
          <b-modal :id="`job-actions-${job.id}`" header-class="border-0" hide-footer lazy>
            <job-actions-modal :job="job" :modalID="`job-actions-${job.id}`"/>
          </b-modal>
        </b-card>
      </b-col>
    </b-row>
  </page>
</template>

<script>
import Markdown from 'vue-markdown';
import { mapRoot, mapPreferences } from '@src/mixins/root';

import AdvancedForm from '@/components/AdvancedForm';
import Page from '@/components/Page';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import LinkedinSearchDetails from '@src/components/jobs/LinkedInSearch';
import JobActionsModal from '@src/modals/job-actions-modal';
import KeyStats from '@/components/jobs/key-stats';
import JobShowQuery from '@/graphql/queries/JobShow.gql';

export default {
  props: {
    jobID: String,
  },
  components: {
    AdvancedForm,
    Page,
    LoadingSpinner,
    Markdown,
    KeyStats,
    LinkedinSearchDetails,
    JobActionsModal,
  },
  data: () => ({
    err: null,
    showApplyBox: false,
    applyResult: null,
    showJobSharedContent: false
  }),
  apollo: {
    job: {
      query: JobShowQuery,
      variables() {
        const { jobID } = this;
        return { jobID };
      },
      update: ({ company: { job } = {} }) => job,
      result({ loading, data }) {
        if (process.env.VUE_ENV === 'client' && !loading && data) {
          if (this.job) {
            const { id: jobID } = this.job;
            try {
              if ([ 'JobShowShare', 'JobShowShareChannelCampaign' ].includes(this.$route.name)) {
                this.$nextTick(() => {
                  const { [`share-${this.job.id}`]: shareRef } = this.$refs;
                  shareRef && shareRef.show();
                });
              }
            } catch (err) {
              this.$notifyErr(err);
              this.$log.error(err);
            }

            if (this.isLoggedIn) {
              this.$root.captureLog({
                eventName: 'JOB_VISITED',
                job: { id: jobID },
                user: { self: true },
              });
            }
          } else {
            this.$notifyFatalErr(new Error('Job not found'));
          }
        }
      },
      error(err) {
        this.$log.error(err);
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      prefetch: true,
    },
  },
  metaInfo() {
    const head = {};

    if (this.job) {
      const { title } = this.job;
      head.title = title;
    }

    return head;
  },
  computed: {
    ...mapRoot([
      'company', 'user', 'isLoggedIn', 'isRecruiter', 'isSuperAdmin', 'isNativeApp',
      'isCommunitySite', 'isSainsburys', 'isPayPal', 'isViooh', 'isAssurant',
      'canSyncContacts', 'origin', 'isHelloFresh'
    ]),
    ...mapPreferences([ 'canEmployeeApply', 'showLinkedInSearchOnJobs' ]),
    ...mapRoot({
      jobShareText({ company }) {
        const { preferences, sitePreferences, ...sharePreferences } = company || {};
        const { customFieldsHash: customFields } = this.job || {};
        const { jobShareText } = { ...preferences, ...sitePreferences, ...sharePreferences, ...customFields };

        if (this.isAssurant && this.user && this.user.locale === 'fr') {
          return 'Assurant recrute ! Nous recherchons un(e) {jobTitle} pour rejoindre notre équipe. Consultez l’annonce et postulez maintenant !';
        }

        if (this.isHelloFresh && this.user && this.user.locale === 'de') {
          return 'Hast du Lust mit mir bei HelloFresh als {jobTitle} zu arbeiten? Jetzt bewerben!';
        }

        return jobShareText || 'Are you interested in working with me as a {jobTitle}?';
      },
    }),
    markdownAnchorAttributes: () => ({
      // Open all hyperlinks in the Markdown block in a new tab
      target: '_blank',
      // Enforce a nofollow to avoid SEO backlash from private content
      rel: 'nofollow',
    }),
    absoluteShareURL() {
      const { origin } = this;
      const { shareCode } = this.job || {};
      if (origin && shareCode) {
        return `${this.origin}/r/${shareCode}`;
      } else {
        return null;
      }
    },
  },
  methods: {
    async trackedLiBrowseEvent() {
      try {
        const { id: jobID, campaign } = this.job;
        const { id: campaignID } = campaign || {};
        await this.$root.captureLog({
          eventName: 'JOB_BROWSE_LINKEDIN',
          campaign: { id: campaignID },
          job: { id: jobID },
          user: { self: true },
        });
      } catch (err) {
        this.$log.error(err);
      }
    },
    async trackJobSharedEvent({ channel }) {
      try {
        const { id: jobID, campaign } = this.job;
        const { id: campaignID } = campaign || {};
        await this.$root.captureLog({
          eventName: 'JOB_SHARED',
          campaign: { id: campaignID },
          job: { id: jobID },
          user: { self: true },
          dataHash: { channel },
        });
      } catch (err) {
        this.$log.error(err);
      }
    },
  },
};
</script>
<style lang="scss">
.page .job-show {

  .loading-panel {
    margin: 20px 0 20px 0;
    text-align: center;
  }

  .horizontal-filter {
    margin-top: 1.4em;
  }
  .alert-danger {
    color: #721c24;
    background-color: #f8d7da;
    border-color: #f5c6cb;
  }

  .sticky-top {
    top: 10px;
    z-index: 900;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/jobs/campaignJobShow.vue